import { useState } from 'react';
import type { QueueTicket, Service, Staff } from '@salonomy/types';
import { Button, Stat, Toggle } from '@salonomy/ui';
import { useQueue, useQueueAction, useToggleQueue } from '../../data/queue.js';
import { useSalon, useStaff } from '../../data/salon.js';
import { useServices } from '../../data/services.js';
import { useToast } from '../../lib/toast.js';
import { env } from '../../lib/env.js';
import { clockLabelAt } from '../../lib/time.js';
import { JoinQueueModal } from './JoinQueueModal.js';

export function QueueRail({ salonSlug, filledToday }: { salonSlug: string; filledToday?: number }) {
  const { data: queue } = useQueue();
  const { data: staff } = useStaff();
  const { data: services } = useServices();
  const { data: salon } = useSalon();
  const toggle = useToggleQueue();
  const [joinOpen, setJoinOpen] = useState(false);
  const tz = salon?.timezone ?? 'Europe/London';

  const tickets = queue?.tickets ?? [];
  const activeId = tickets.find((t) => t.status === 'waiting' || t.status === 'called')?.id;
  const shareUrl = `${env.queuePublicBaseUrl.replace(/^https?:\/\//, '')}/q/${salonSlug}`;

  return (
    <aside className="qrail">
      <div className="qrail__head">
        <div className="qrail__title">
          <span className={`qrail__live-dot${queue?.open ? '' : ' qrail__live-dot--off'}`} />
          Live queue
        </div>
        <div className="qrail__open">
          <span>{queue?.open ? 'Open' : 'Closed'}</span>
          <Toggle
            checked={Boolean(queue?.open)}
            onChange={(v) => toggle.mutate(v)}
            label="Toggle queue open"
            labelHidden
          />
        </div>
      </div>

      <div className="qrail__stats">
        <Stat label="Waiting" value={queue?.waitingCount ?? 0} />
        <Stat label="Avg wait" value={queue?.avgWaitMin != null ? `${queue.avgWaitMin}m` : '—'} />
        <Stat label="Filled today" value={filledToday ?? 0} />
      </div>

      <div className="qrail__list">
        {tickets.length === 0 ? (
          <div className="muted" style={{ fontSize: 13, padding: '8px 2px' }}>
            {queue?.open ? 'No one waiting right now.' : 'The queue is closed.'}
          </div>
        ) : (
          tickets.map((t) => (
            <QueueTicketCard
              key={t.id}
              ticket={t}
              staff={staff ?? []}
              services={services ?? []}
              tz={tz}
              active={t.id === activeId}
            />
          ))
        )}
      </div>

      <Button variant="outline" size="sm" onClick={() => setJoinOpen(true)} disabled={!queue?.open}>
        + Add walk-in
      </Button>

      <div className="qshare">
        <span className="qshare__title">Share today’s queue</span>
        <div className="qshare__link">
          <span className="qshare__url">{shareUrl}</span>
          <CopyButton text={`${env.queuePublicBaseUrl}/q/${salonSlug}`} />
        </div>
        <span className="qshare__blurb">
          On your Instagram bio, Google profile and front-window QR. Clients join without an app.
        </span>
      </div>

      <a
        className="qrail__cta"
        href={`${env.queuePublicBaseUrl}/q/${salonSlug}`}
        target="_blank"
        rel="noreferrer"
        style={{ textAlign: 'center', fontSize: 13, fontWeight: 600, color: 'var(--slm-cobalt)' }}
      >
        Open public board →
      </a>

      {joinOpen ? (
        <JoinQueueModal
          staff={staff ?? []}
          services={services ?? []}
          onClose={() => setJoinOpen(false)}
        />
      ) : null}
    </aside>
  );
}

export function QueueTicketCard({
  ticket,
  staff,
  services,
  tz,
  active,
}: {
  ticket: QueueTicket;
  staff: Staff[];
  services: Service[];
  tz: string;
  active: boolean;
}) {
  const action = useQueueAction();
  const staffName = ticket.requestedStaffId
    ? (staff.find((s) => s.id === ticket.requestedStaffId)?.displayName ?? null)
    : null;
  const serviceName = ticket.serviceId
    ? (services.find((s) => s.id === ticket.serviceId)?.name ?? null)
    : null;

  const title =
    `${ticket.clientName}` +
    (serviceName ? ` — ${serviceName}` : staffName ? ` — with ${staffName}` : ' — any chair');

  const eta = ticket.etaAt ? clockLabelAt(ticket.etaAt, tz) : null;
  const meta =
    ticket.status === 'in_chair'
      ? { text: 'In the chair', cls: 'qticket__meta--inchair' }
      : ticket.status === 'called'
        ? { text: eta ? `Called · ETA ${eta}` : 'Called', cls: '' }
        : { text: eta ? `ETA ${eta}` : `Waiting · #${ticket.position}`, cls: '' };

  return (
    <div className={`qticket${active ? ' qticket--active' : ''}`}>
      <span className="qticket__pos">{ticket.position || '·'}</span>
      <div className="qticket__body">
        <span className="qticket__name">{title}</span>
        <span className={`qticket__meta ${meta.cls}`}>{meta.text}</span>
      </div>
      <div className="qticket__actions">
        {ticket.status === 'waiting' ? (
          <Button size="sm" onClick={() => action.mutate({ id: ticket.id, action: 'call' })}>
            Call up
          </Button>
        ) : ticket.status === 'called' ? (
          <>
            <Button
              size="sm"
              variant="soft"
              onClick={() => action.mutate({ id: ticket.id, action: 'complete' })}
            >
              Done
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => action.mutate({ id: ticket.id, action: 'no_show' })}
            >
              No-show
            </Button>
          </>
        ) : null}
      </div>
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const toast = useToast();
  return (
    <button
      className="qshare__copy"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          toast.success('Queue link copied');
        } catch {
          toast.error('Could not copy — long-press to copy');
        }
      }}
    >
      Copy
    </button>
  );
}
